import React, { useState, useEffect } from 'react';
import plotService from '../../services/plotService';

const SECTION_OPTIONS = [
  'LA-3000 A','LA-3000 B','LA-3000 C','LA-3000 E','LA-3000 F',
  'LA-6000 A','LA-6000 B','LA-6000 D','LA-6000 C',
  'LA-4000','LA-7000',
  'LA-5000 A','LA-5000 B','LA-5000 C','LA-5000 D','LA-5000 E','LA-5000 F',
  'CM-4C','CM-8D','CM-12',
  'FM-3A','FM-4A','FM-5A','FM-6A','FM-3B',
  'JM-41','JM-411',
  'LA-1000 A','LA-1000 B','LA-1000 C','LA-1000 D','LA-1000 E','LA-1000 F',
  'JM-12','JM-8',
  'LA-9000','LA-12000',
  'LA-1100 A','LA-1100 B',
  'FE-16 A','FE-24','FE-16 B',
  'LA-2000 A','LA-2000 B',
  'MT-40','MT-30',
  'M4-K6','M4-K7',
];

const PlotForm = ({ plot, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    plot_number: '',
    section: '',
    row_number: '',
    column_number: '',
    latitude: '',
    longitude: '',
    status: 'available',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAutoGenerated, setIsAutoGenerated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [allPlots, setAllPlots] = useState([]);

  // Populate form when editing or auto-generate plot number when creating
  useEffect(() => {
    if (plot) {
      // Editing existing plot
      setFormData({
        plot_number: plot.plot_number || '',
        section: plot.section || '',
        row_number: plot.row_number || '',
        column_number: plot.column_number || '',
        latitude: plot.latitude || '',
        longitude: plot.longitude || '',
        status: plot.status || 'available',
        notes: plot.notes || '',
      });
      setIsAutoGenerated(false);
    } else {
      // Creating new plot - auto-generate plot number
      generateNextPlotNumber();
    }
  }, [plot]);

  // Load all existing plots for duplicate checking
  useEffect(() => {
    const loadAllPlots = async () => {
      try {
        const res = await plotService.getAll({ per_page: 9999 });
        if (res.success) setAllPlots(res.data?.data ?? []);
      } catch (_) {
        // non-critical
      }
    };
    loadAllPlots();
  }, []);

  const generateNextPlotNumber = async () => {
    try {
      // Fetch all plots to find the highest number
      let allPlots = [];
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await plotService.getAll({ page, per_page: 100 });
        
        // The API returns { success, data: { data: [...], current_page, last_page, ... } }
        if (response.success && response.data && response.data.data && response.data.data.length > 0) {
          allPlots = allPlots.concat(response.data.data);
          
          // Check if there are more pages
          if (response.data.current_page < response.data.last_page) {
            page++;
          } else {
            hasMorePages = false;
          }
        } else {
          hasMorePages = false;
        }
      }

      let nextNumber = 1;
      
      if (allPlots.length > 0) {
        // Find the plot with the highest number
        const plotNumbers = allPlots
          .map(p => {
            const match = p.plot_number.match(/\d+/);
            return match ? parseInt(match[0]) : 0;
          })
          .filter(n => n > 0);
        
        if (plotNumbers.length > 0) {
          nextNumber = Math.max(...plotNumbers) + 1;
        }
      }

      const newPlotNumber = `PLT-${String(nextNumber).padStart(4, '0')}`;
      setFormData(prev => ({ ...prev, plot_number: newPlotNumber }));
      setIsAutoGenerated(true);
    } catch (err) {
      console.error('Error generating plot number:', err);
      setIsAutoGenerated(false);
    }
  };

  // Real-time field validation
  const validateField = (name, value) => {
    let error = null;
    switch (name) {
      case 'section':
        if (!value.trim()) error = 'Section is required';
        break;
      case 'row_number':
        if (value === '' || value === null || value === undefined) error = 'Row number is required';
        else if (parseInt(value) < 1) error = 'Row number must be at least 1';
        break;
      case 'column_number':
        if (value === '' || value === null || value === undefined) error = 'Column number is required';
        else if (parseInt(value) < 1) error = 'Column number must be at least 1';
        break;
      case 'latitude':
        if (value !== '' && value !== undefined) {
          const lat = parseFloat(value);
          if (isNaN(lat)) error = 'Latitude must be a valid number (e.g., 14.5558893)';
          else if (lat < -90 || lat > 90) error = 'Latitude must be between -90 and 90';
        }
        break;
      case 'longitude':
        if (value !== '' && value !== undefined) {
          const lng = parseFloat(value);
          if (isNaN(lng)) error = 'Longitude must be a valid number (e.g., 121.0244567)';
          else if (lng < -180 || lng > 180) error = 'Longitude must be between -180 and 180';
        }
        break;
      default:
        break;
    }
    setValidationErrors(prev => {
      const updated = { ...prev };
      if (error) { updated[name] = error; } else { delete updated[name]; }
      return updated;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    setFormData(newData);
    validateField(name, value);

    if (['section', 'row_number', 'column_number'].includes(name)) {
      const { section, row_number, column_number } = newData;
      if (section && row_number && column_number) {
        const currentId = plot?.id ?? null;
        const isDup = allPlots.some(
          p =>
            p.section === section &&
            parseInt(p.row_number) === parseInt(row_number) &&
            parseInt(p.column_number) === parseInt(column_number) &&
            p.id !== currentId
        );
        setValidationErrors(prev => {
          const updated = { ...prev };
          if (isDup) {
            updated.duplicate = `Row ${row_number}, Column ${column_number} already exists in section "${section}".`;
          } else {
            delete updated.duplicate;
          }
          return updated;
        });
      } else {
        setValidationErrors(prev => { const u = { ...prev }; delete u.duplicate; return u; });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const newErrors = {};

    if (!formData.section || !formData.section.trim()) {
      newErrors.section = 'Section is required';
    }

    if (formData.row_number === '' || formData.row_number === null || formData.row_number === undefined) {
      newErrors.row_number = 'Row number is required';
    } else if (parseInt(formData.row_number) < 1) {
      newErrors.row_number = 'Row number must be at least 1';
    }

    if (formData.column_number === '' || formData.column_number === null || formData.column_number === undefined) {
      newErrors.column_number = 'Column number is required';
    } else if (parseInt(formData.column_number) < 1) {
      newErrors.column_number = 'Column number must be at least 1';
    }


    // Validate latitude - accept decimal numbers between -90 and 90
    if (!formData.latitude && formData.latitude !== 0) {
      newErrors.latitude = 'Latitude is required';
    } else {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat)) {
        newErrors.latitude = 'Latitude must be a valid number (e.g., 14.5558893)';
      } else if (lat < -90 || lat > 90) {
        newErrors.latitude = 'Latitude must be between -90 and 90';
      }
    }

    // Validate longitude - accept decimal numbers between -180 and 180
    if (!formData.longitude && formData.longitude !== 0) {
      newErrors.longitude = 'Longitude is required';
    } else {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng)) {
        newErrors.longitude = 'Longitude must be a valid number (e.g., 121.0244567)';
      } else if (lng < -180 || lng > 180) {
        newErrors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    // If there are validation errors, display them and don't submit
    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      return;
    }

    if (validationErrors.duplicate) {
      return;
    }

    setValidationErrors({});
    setLoading(true);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save plot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}
      
      <div className="form-row">
        <div className="form-group">
          <label>Plot Number *</label>
          <input
            type="text"
            name="plot_number"
            className="form-control"
            value={formData.plot_number}
            onChange={handleChange}
            placeholder="e.g., PLT-0001"
            readOnly
            required
            style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
          />
          {isAutoGenerated && !plot && <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>Auto-generated based on latest plot</small>}
        </div>
        <div className="form-group">
          <label>Section</label>
          <input
            type="text"
            list="plot-form-section-options"
            name="section"
            className={`form-control ${validationErrors.section ? 'error' : ''}`}
            value={formData.section}
            onChange={handleChange}
            placeholder="Select or type a section"
          />
          <datalist id="plot-form-section-options">
            {SECTION_OPTIONS.map(s => <option key={s} value={s} />)}
          </datalist>
          {validationErrors.section && (
            <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
              {validationErrors.section}
            </small>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Row Number</label>
          <input
            type="number"
            name="row_number"
            className={`form-control ${validationErrors.row_number ? 'error' : ''}`}
            value={formData.row_number}
            onChange={handleChange}
            min="1"
          />
          {validationErrors.row_number && (
            <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
              {validationErrors.row_number}
            </small>
          )}
        </div>
        <div className="form-group">
          <label>Column Number</label>
          <input
            type="number"
            name="column_number"
            className={`form-control ${validationErrors.column_number ? 'error' : ''}`}
            value={formData.column_number}
            onChange={handleChange}
            min="1"
          />
          {validationErrors.column_number && (
            <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
              {validationErrors.column_number}
            </small>
          )}
        </div>
      </div>

      {validationErrors.duplicate && (
        <div style={{
          padding: '10px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          fontSize: '0.875rem',
          marginBottom: '12px',
          border: '1px solid #ffc107',
        }}>
          ⚠️ {validationErrors.duplicate}
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label>Latitude *</label>
          <input
            type="number"
            name="latitude"
            className={`form-control ${validationErrors.latitude ? 'error' : ''}`}
            value={formData.latitude}
            onChange={handleChange}
            step="0.00000001"
            placeholder="e.g., 14.5547"
            required
          />
          {validationErrors.latitude && (
            <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
              {validationErrors.latitude}
            </small>
          )}
        </div>
        <div className="form-group">
          <label>Longitude *</label>
          <input
            type="number"
            name="longitude"
            className={`form-control ${validationErrors.longitude ? 'error' : ''}`}
            value={formData.longitude}
            onChange={handleChange}
            step="0.00000001"
            placeholder="e.g., 121.0244"
            required
          />
          {validationErrors.longitude && (
            <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
              {validationErrors.longitude}
            </small>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>Status</label>
        <select
          name="status"
          className="form-control"
          value={formData.status}
          onChange={handleChange}
        >
          <option value="available">Available</option>
          <option value="occupied">Occupied</option>
          <option value="reserved">Reserved</option>
          <option value="maintenance">Maintenance</option>
        </select>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea
          name="notes"
          className={`form-control ${validationErrors.notes ? 'error' : ''}`}
          value={formData.notes}
          onChange={handleChange}
          rows="3"
        />
        {validationErrors.notes && (
          <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
            {validationErrors.notes}
          </small>
        )}
      </div>

      <div className="modal-footer" style={{ padding: '20px 0 0 0', borderTop: 'none' }}>
        <button type="button" className="btn" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || Object.keys(validationErrors).length > 0}>
          {loading ? 'Saving...' : (plot ? 'Update' : 'Create')}
        </button>
      </div>
    </form>
  );
};

export default PlotForm;
